// src/pages/OrgAssessments.tsx
import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { ClientAdminBreadcrumb } from "@/components/layout/ClientAdminBreadcrumb";
import { AdminBreadcrumb, AdminBreadcrumbSegment } from "@/components/layout/AdminBreadcrumb";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FileText, Building2 } from "lucide-react";
import { BrandedLogo } from "@/components/ui/BrandedLogo";
import { getLogoUrl } from "@/utils/getLogoUrl";
import { PortfolioUsersPanel } from "@/components/portfolio/PortfolioUsersPanel";
import { PortfolioAssessmentsPanel } from "@/components/portfolio/PortfolioAssessmentsPanel";
import { isAdminEffective, adminGetAssessmentOverview } from "@/lib/admin";

interface AdminContext {
  clientId: string;
  clientName: string;
  subOrgId: string;
  subOrgName: string;
  portfolioId: string;
  portfolioName: string;
}

export default function OrgAssessments() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { role: effectiveRole } = useEffectiveRole();
  
  // Get admin context from navigation state
  const adminContext = (location.state as { adminContext?: AdminContext } | null)?.adminContext;
  
  const [orgName, setOrgName] = useState<string>("");
  const [orgCategory, setOrgCategory] = useState<string | null>(null);
  const [questionnaireId, setQuestionnaireId] = useState<string | null>(null);
  const [subOrgId, setSubOrgId] = useState<string | null>(null);
  const [subOrgName, setSubOrgName] = useState<string>("");
  const [clientName, setClientName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  
  // PM context header (persistent across pages)
  const [pmContext, setPmContext] = useState<{ clientName: string | null; subOrgName: string | null; clientLogo: string | null }>({
    clientName: null,
    subOrgName: null,
    clientLogo: null,
  });

  // Primary load: portfolio data (blocking) - assessments load first
  useEffect(() => {
    (async () => {
      if (!orgId) return;
      setLoading(true);
      setErr(null);
      try {
        const isAdmin = await isAdminEffective();
        
        let portfolio: { 
          name: string; 
          category: string | null; 
          questionnaire_id: string | null; 
          sub_org_id: string | null;
          sub_org_name?: string | null;
          client_id?: string | null;
        } | null = null;
        
        if (isAdmin) {
          console.log("[ADMIN] RPC=admin_get_portfolio_summary portfolioId=", orgId);
          const { data, error } = await (supabase.rpc as any)("admin_get_portfolio_summary", { 
            p_portfolio_id: orgId 
          });
          if (error) {
            console.warn("[ADMIN] admin_get_portfolio_summary error:", error);
          }
          const row = data?.[0];
          if (row) {
            portfolio = { 
              name: row.name, 
              category: row.category, 
              questionnaire_id: row.questionnaire_id, 
              sub_org_id: row.sub_org_id,
              sub_org_name: row.sub_org_name ?? null,
              client_id: row.client_id ?? null,
            };
          }
        } else {
          // PM: use pm_get_portfolio_by_id RPC (security-definer, scoped to PM's sub-orgs)
          const { data: portfolioRows, error: portfolioErr } = await (supabase.rpc as any)(
            "pm_get_portfolio_by_id",
            { p_portfolio_id: orgId }
          );
          if (portfolioErr) {
            console.warn(portfolioErr);
          }
          const row = portfolioRows?.[0];
          portfolio = row ? { name: row.name, category: row.category, questionnaire_id: row.questionnaire_id, sub_org_id: row.sub_org_id } : null;
        }
        
        setOrgName(portfolio?.name ?? "");
        setOrgCategory(portfolio?.category ?? null);
        setQuestionnaireId(portfolio?.questionnaire_id ?? null);
        setSubOrgId(portfolio?.sub_org_id ?? null);
        
        // For admins: use the RPC data directly for sub_org_name
        if (isAdmin && portfolio) {
          setSubOrgName(portfolio.sub_org_name ?? "");
        }
      } catch (e: any) {
        console.warn(e);
        setErr("Failed to load portfolio.");
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId]);

  // NON-BLOCKING: Client name lookup (runs after primary load, does not block assessments)
  useEffect(() => {
    if (!subOrgId && !orgId) return;
    
    (async () => {
      try {
        const isAdmin = await isAdminEffective();
        
        if (isAdmin) {
          // Admin: get client_id from portfolio summary RPC, then fetch client name via RPC
          const { data } = await (supabase.rpc as any)("admin_get_portfolio_summary", { p_portfolio_id: orgId });
          const clientId = data?.[0]?.client_id;
          if (clientId) {
            const { data: clientData } = await (supabase as any).rpc("admin_get_client_name", { p_client_id: clientId });
            setClientName(clientData?.[0]?.name ?? "");
          }
        } else if (subOrgId) {
          // Non-admin (PM): use pm_get_sub_org_by_id RPC
          const { data: subOrgRows } = await (supabase.rpc as any)(
            "pm_get_sub_org_by_id",
            { p_sub_org_id: subOrgId }
          );
          const subOrg = subOrgRows?.[0];
          if (!subOrgName) setSubOrgName(subOrg?.name ?? "");
          
          if (subOrg?.client_id) {
            const { data: clientRows } = await (supabase.rpc as any)(
              "pm_get_client_by_id",
              { p_client_id: subOrg.client_id }
            );
            setClientName(clientRows?.[0]?.name ?? "");
          }
        }
      } catch (e) {
        // Non-blocking: log but don't break page
        console.warn("[OrgAssessments] Client name lookup failed (non-blocking):", e);
      }
    })();
  }, [orgId, subOrgId, subOrgName]);

  // NON-BLOCKING: PM context header
  useEffect(() => {
    if (effectiveRole !== "portfolio_manager" || !orgId) return;
    
    (async () => {
      try {
        const { data: pmData } = await (supabase as any).rpc("pm_portfolio_context", { _portfolio_id: orgId });
        if (pmData && pmData.length > 0) {
          setPmContext({
            clientName: pmData[0].client_name || null,
            subOrgName: pmData[0].sub_org_name || null,
            clientLogo: pmData[0].client_logo || null,
          });
        }
      } catch (e) {
        // Non-blocking: log but don't break page
        console.warn("[OrgAssessments] PM context lookup failed (non-blocking):", e);
      }
    })();
  }, [effectiveRole, orgId]);

  const handleBack = () => {
    if (effectiveRole === "admin") {
      // Navigate back to admin console - the breadcrumb handles granular navigation
      navigate("/admin");
    } else if (effectiveRole === "portfolio_manager") {
      navigate("/pm");
    } else if (effectiveRole === "client_admin" && subOrgId) {
      navigate(`/client/sub-orgs/${subOrgId}/portfolios`);
    } else {
      navigate("/org-admin");
    }
  };

  const backLabel = effectiveRole === "admin"
    ? "Back to Admin Console"
    : effectiveRole === "portfolio_manager" 
    ? "Back to Portfolio Dashboard" 
    : effectiveRole === "client_admin"
    ? "Back to Portfolios"
    : "Back to Org Admin";

  const isPM = effectiveRole === "portfolio_manager";
  const isClientAdmin = effectiveRole === "client_admin";
  const isAdmin = effectiveRole === "admin";

  // Build breadcrumb segments for client_admin
  const breadcrumbSegments = isClientAdmin && subOrgId
    ? [
        { label: subOrgName || "Sub-Org", href: `/client/sub-orgs/${subOrgId}/portfolios` },
        { label: orgName || "Portfolio" },
      ]
    : undefined;

  // Build admin breadcrumb segments with proper navigation state
  // We use query params to encode the parent context for navigation
  const adminBreadcrumbSegments: AdminBreadcrumbSegment[] | undefined = isAdmin && adminContext
    ? [
        { 
          label: adminContext.clientName, 
          href: "/admin",
          // Signal to go back to client level (subOrgs view)
          state: { navigateTo: "subOrgs", clientId: adminContext.clientId, clientName: adminContext.clientName }
        },
        { 
          label: adminContext.subOrgName, 
          href: "/admin",
          // Signal to go back to subOrg level (portfolios view)
          state: { navigateTo: "portfolios", clientId: adminContext.clientId, clientName: adminContext.clientName, subOrgId: adminContext.subOrgId, subOrgName: adminContext.subOrgName }
        },
        { label: adminContext.portfolioName || orgName },
      ]
    : undefined;

  return (
    <AppLayout title="Org Assessments">
      <div className="p-6 space-y-4">
        {/* PM persistent org context header */}
        {isPM && (pmContext.clientName || pmContext.subOrgName) && (
          <div className="flex items-center gap-3 text-sm">
            <BrandedLogo
              src={getLogoUrl(pmContext.clientLogo)}
              alt={pmContext.clientName || "Client"}
              className="h-10 w-auto"
              fallback={<Building2 className="h-5 w-5 text-muted-foreground" />}
            />
            <span className="font-semibold text-foreground">
              {pmContext.clientName && pmContext.subOrgName 
                ? `${pmContext.clientName} - ${pmContext.subOrgName}`
                : pmContext.clientName || pmContext.subOrgName
              }
            </span>
          </div>
        )}
        
        {isAdmin && adminBreadcrumbSegments && (
          <AdminBreadcrumb segments={adminBreadcrumbSegments} />
        )}
        {isClientAdmin && breadcrumbSegments && (
          <ClientAdminBreadcrumb clientName={clientName || "Client"} segments={breadcrumbSegments} />
        )}

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">{orgName || "Organization"}</h1>
            <p className="text-sm text-muted-foreground">Manage this portfolio company</p>
            {/* Read-only questionnaire info for client_admin */}
            {isClientAdmin && orgCategory && questionnaireId && (
              <div className="flex items-center gap-2 mt-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Questionnaire: {orgCategory} – {questionnaireId}
                </span>
                <Badge variant="secondary" className="text-xs">View Only</Badge>
              </div>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {backLabel}
          </Button>
        </div>

        {err && (
          <Alert variant="destructive">
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}

        {!loading && orgId && (
          <Tabs defaultValue="assessments" className="w-full">
            <TabsList>
              <TabsTrigger value="assessments">Assessments</TabsTrigger>
              {(isPM || isAdmin || isClientAdmin) && <TabsTrigger value="users">Users</TabsTrigger>}
            </TabsList>

            <TabsContent value="assessments" className="mt-4">
              <PortfolioAssessmentsPanel portfolioId={orgId} portfolioName={orgName} category={orgCategory} />
            </TabsContent>

            {(isPM || isAdmin || isClientAdmin) && (
              <TabsContent value="users" className="mt-4">
                <PortfolioUsersPanel portfolioId={orgId} portfolioName={orgName} category={orgCategory} />
              </TabsContent>
            )}
          </Tabs>
        )}
      </div>
    </AppLayout>
  );
}
